
import {
  computed, defineComponent, onMounted, PropType, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { useMainStore } from '@/store/mainStore';
import {
  SHARE_ACCESS, MESSAGE_RECEIVER, SHARE_TYPES, SHARE_SETTINGS, NAVIGATION_PAGES, PHOTO_VIEW_TYPE,
} from '@/types/enums';
import {
  IGroup, INavigation, IRequestSpacesContact, ISelectCaseMessageReciever, ISpacesContact,
} from '@/types/interfaces';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import AppSearch from '@/components/UI/AppSearch.vue';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import AppMessageShareSettings from '@/components/AppMessageShareSettings.vue';
import AppNoMessage from '@/components/AppNoMessage.vue';
import AppNavigation from '@/components/AppNavigation.vue';

export default defineComponent({
  components: {
    AppNavigation,
    AppNoMessage,
    // BottomPopUpItemWrapper,
    BottomPopUpItem,
    AppSearch,
    AppLazyImage,
    AppMessageShareSettings,
  },
  props: {
    groupList: { type: Object as () => IGroup[], required: true },
    contactList: { type: Object as () => IRequestSpacesContact[], required: true },
    itemId: { type: String, required: true },
    itemType: { type: Number as PropType<SHARE_TYPES>, required: true },
    itemOwnerId: { type: String, required: true },
    itemShareSettings: { type: Array as () => SHARE_SETTINGS[] },
  },
  emits: ['back', 'onShare'],
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const router = useRouter();
    const selectedItem = ref<ISelectCaseMessageReciever | null>(null);
    const search = ref('');
    const shareSettings = ref<SHARE_SETTINGS[]>([]);

    const isMy = computed(() => props.itemOwnerId === mainStore.user.accountId);
    const sortedUsersAndGroups = computed<any[]>(() => [...props.groupList, ...props.contactList]);

    const selectedType = ref('contacts');
    const navigationData: INavigation[] = [
      {
        label: 'contacts.toggle.contacts',
        routeTo: 'contacts',
        activePage: NAVIGATION_PAGES.SPACES_CONTACTS,
      },
      {
        label: 'contacts.toggle.groups',
        routeTo: 'groups',
        activePage: NAVIGATION_PAGES.SPACES_GROUPS_MAIN,
      },
    ];

    function onSelectGroup(group: IGroup) {
      if (selectedItem.value?.id === group.id) {
        selectedItem.value = null;
      } else {
        selectedItem.value = { type: MESSAGE_RECEIVER.GROUP, id: group.id };
      }
    }

    function onSelectContact(contact: ISpacesContact) {
      if (selectedItem.value?.id === contact.accountId) {
        selectedItem.value = null;
      } else {
        selectedItem.value = { type: MESSAGE_RECEIVER.CONTACT, id: contact.accountId };
      }
    }

    function resolveFullName(contact: ISpacesContact): string {
      return `${contact.firstName} ${contact.lastName}`;
    }

    function resolveGroupPhotoUrl(group: IGroup): string {
      if (group.photo?.url) {
        return lowResUrlByViewTypeByUrl(group.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/Group.svg');
    }

    function resolveContactPhotoUrl(contact: ISpacesContact): string {
      if (contact.photo?.url) {
        return lowResUrlByViewTypeByUrl(contact.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    }
    const shareUrl = computed(() => {
      const isGroup = selectedItem.value?.type === MESSAGE_RECEIVER.GROUP;
      const preUrl = (isGroup ? '/messages/group/' : '/messages/') + selectedItem.value?.id;

      if (props.itemType === SHARE_TYPES.CASE) {
        return `${preUrl}?sendCase=${props.itemId}&shareSettings=${JSON.stringify(shareSettings.value)}`;
      }
      if (props.itemType === SHARE_TYPES.FOLDER) {
        return `${preUrl}?sendFolder=${props.itemId}`;
      }
      return `${preUrl}?sendFile=${props.itemId}&shareSettings=${JSON.stringify(shareSettings.value)}`;
    });

    // const filterGroups = computed(() => props.groupList.filter((gr) => gr.name.toLowerCase().includes(search.value.toLowerCase())));
    // const filterContacts = computed(() => props.contactList.filter((ct) => resolveFullName(ct).toLowerCase().includes(search.value.toLowerCase())));
    const filteredUsersAndGroups = computed(() => {
      const items = sortedUsersAndGroups.value.map((item) => ({ ...item as any, searchString: (item as any).targetType !== 'GROUP' ? resolveFullName(item) : (item as any).name }));
      return items.filter((el) => el.searchString.toLowerCase().includes(search.value.toLowerCase()));
    });

    const filteredUsers = computed(() => {
      const items = sortedUsersAndGroups.value
        .filter((el) => (el as any).targetType !== 'GROUP')
        .map((item) => ({ ...item.user as any, searchString: resolveFullName(item.user) }));
      return items.filter((el) => el.searchString.toLowerCase().includes(search.value.toLowerCase()));
    });

    const filteredGroups = computed(() => {
      const items = sortedUsersAndGroups.value.filter((el) => (el as any).targetType === 'GROUP').map((item) => ({ ...item as any, searchString: (item as any).name }));
      return items.filter((el) => el.searchString.toLowerCase().includes(search.value.toLowerCase()));
    });

    const noContactsFound = computed(() => {
      if (!filteredUsersAndGroups.value.length) {
        return {
          text: 'No search results found',
          icon: 'icon-not-found',
        };
      }
      return '';
    });

    function onShare() {
      if (!selectedItem.value) {
        return;
      }
      // selectedItem.value.forEach((id) => {
      //   WebsocketService.sendMedia(messagesStore!.currentConversation!.info.id, id, props.isGroup);
      // });
      router.push(shareUrl.value);
      emit('onShare');
    }

    function setShareSettings() {
      if (props.itemShareSettings && !isMy.value) {
        shareSettings.value = props.itemShareSettings;
      }
    }

    function updateShareSettings(event) {
      shareSettings.value = [
        event.allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        event.allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        event.allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    }

    onMounted(() => {
      setShareSettings();
      shareSettings.value = [
        mainStore.sharingSettings[0].allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        mainStore.sharingSettings[0].allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        mainStore.sharingSettings[0].allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    });

    return {
      router,
      selectedItem,
      shareUrl,
      search,
      // filterGroups,
      // filterContacts,
      filteredUsersAndGroups,
      sortedUsersAndGroups,
      noContactsFound,

      navigationData,
      selectedType,

      resolveFullName,
      resolveGroupPhotoUrl,
      resolveContactPhotoUrl,
      onSelectGroup,
      onSelectContact,
      onShare,
      updateShareSettings,
      SHARE_ACCESS,
      isMy,
      shareSettings,
      filteredUsers,
      filteredGroups,
    };
  },
});
